import { AxisBottom, AxisLeft, AxisRight } from '@visx/axis';
import { Group } from '@visx/group';
import { ParentSize } from '@visx/responsive';
import { scaleBand, scaleLinear, scaleOrdinal } from '@visx/scale';
import { LinePath } from '@visx/shape';
import React from 'react';
import { BAND_AXIS_GUTTER, BAND_ICON_BOX, BandThresholdAxisIcon } from './BandThresholdAxisIcon';
import { BandedMetric, bandThresholdMarkers } from './bandVisuals.utils';
import { isSeriesEntirelyUnmeasured } from './breakdownMetricBinding.utils';
import { bandedAxisTickLabel, bandedAxisTicks, chartYDomain } from './chartYDomain.utils';

/** Said in place of a trend chart that has nothing to draw. */
export const TREND_CHART_EMPTY_MESSAGE = 'No trend data available for the current filters.';

export function MultiSeriesTrendChart({
  series,
  bandMetric,
  bandAxisSide = 'left',
}: {
  series: Array<{ label: string; color: string; points: { x: string; value?: number }[] }>;
  /** Set only for metrics that carry bands, which is what puts boundaries on the axis. */
  bandMetric?: BandedMetric;
  /**
   * Which axis carries the bands.
   *
   * `left` when the whole chart is that one banded metric, so the boundaries belong on the chart's
   * only axis. `right` when the banded series shares the panel with unbanded ones — the left axis
   * stays a plain percentage scale for everything, and the bands get a second axis of their own so
   * they cannot be read as applying to series that have no bands.
   */
  bandAxisSide?: 'left' | 'right';
}) {
  // No series, or none with a single reading: a window in which nothing was measured has no trend to
  // show, and axes and a legend over nothing but gaps would read as a broken chart.
  if (series.every((entry) => isSeriesEntirelyUnmeasured(entry.points))) {
    return <div className="text-sm text-slate-500">{TREND_CHART_EMPTY_MESSAGE}</div>;
  }

  const labels = series[0]?.points.map((point) => point.x) || [];
  // Boundaries come from the shared bands; this chart holds no thresholds of its own.
  const thresholdMarkers = bandMetric ? bandThresholdMarkers(bandMetric) : [];

  return (
    /**
     * A column whose height is the legend's natural height PLUS the plot's own fixed height.
     *
     * The outer box deliberately sets no height at all. It grows with the legend, so a legend that
     * wraps onto four lines pushes nothing outside the card — the original overflow this file was
     * restructured to fix, which came from a fixed-height box holding a natural-height legend
     * followed by a plot that claimed the whole box for itself.
     *
     * What that restructure got wrong is why the charts then rendered blank: it sized the plot with
     * `flex-1` inside a `min-h-[360px]` column. `min-height` does NOT make a box's height DEFINITE
     * in the CSS sense — it only clamps an otherwise-computed height. A flex item's `flex-basis: 0`
     * plus `flex-grow` distributes the container's FREE SPACE, and a container whose height is
     * indefinite (auto, however clamped) has no free space to distribute, so the item fell back to
     * its content height. `ParentSize` then rendered its own `height: 100%` div inside it, and a
     * percentage height against an indefinite containing block resolves to `auto` — so it measured
     * zero, and every chart drew at zero height.
     *
     * The plot region therefore carries a DEFINITE height of its own (`h-[320px]`), which depends on
     * no ancestor: `ParentSize`'s `height: 100%` resolves against it, the observer measures 320, and
     * the chart draws. Both goals hold at once — a definite basis for the measurement, and an outer
     * box free to grow with the legend.
     */
    <div className="w-full px-3 flex flex-col">
      {/* Natural height, and never compressed: a legend the reader cannot finish reading is worse
          than a slightly shorter plot. */}
      <div className="mb-2 flex flex-wrap items-center gap-3 flex-shrink-0">
        {series.map((entry) => (
          <div key={`legend:${entry.label}`} className="flex items-center gap-2 text-xs text-slate-600">
            <span className="inline-block rounded-full" style={{ width: '10px', height: '10px', backgroundColor: entry.color }} />
            <span>{`${entry.label}`}</span>
          </div>
        ))}
      </div>
      {/*
       * A DEFINITE height, and deliberately not `flex-1` or a `min-h`.
       *
       * This is the only thing `ParentSize` can measure against: it renders a `height: 100%` div,
       * and a percentage height needs a containing block with a definite height to resolve against.
       * `min-height` and flex growth inside an auto-height column both leave that indefinite, which
       * is what made the charts measure zero. 320px is what the plot effectively occupied before,
       * so the drawn chart is the size it has always been.
       */}
      <div className="h-[320px]">
        <ParentSize>
          {({ width, height }) => {
            // The band axis needs its own gutter: the icons are wider than a "100%" label and were
            // being clipped by the margin sized for text alone.
            const bandsOnRight = !!bandMetric && bandAxisSide === 'right';
            const bandsOnLeft = !!bandMetric && bandAxisSide !== 'right';
            const margin = {
              top: 20,
              right: bandsOnRight ? BAND_AXIS_GUTTER : 30,
              bottom: 40,
              left: bandsOnLeft ? BAND_AXIS_GUTTER : 60,
            };
            const innerWidth = Math.max(120, width - margin.left - margin.right);
            const innerHeight = Math.max(120, height - margin.top - margin.bottom);

            const xScale = scaleBand<string>({ domain: labels, range: [0, innerWidth], padding: 0.25 });
            // Every metric is zoomed. Against a fixed 0-100 axis the movement these charts exist to
            // show — a few points either way — renders as a straight line.
            const { min: yMin, max: yMax } = chartYDomain(
              // Only measured points size the window: an absent one has no position to include.
              series.flatMap((entry) => entry.points.map((point) => point.value).filter((value): value is number => value !== undefined)),
              thresholdMarkers.map((marker) => marker.percent)
            );
            // No `nice` rounding: it would widen the window back out and undo the zoom.
            const yScoreScale = scaleLinear<number>({ domain: [yMin, yMax], range: [innerHeight, 0] });
            const visibleThresholds = thresholdMarkers.filter((marker) => marker.percent >= yMin && marker.percent <= yMax);
            const axisTicks = bandedAxisTicks(
              yMin,
              yMax,
              visibleThresholds.map((marker) => marker.percent)
            );
            const colorScale = scaleOrdinal<string, string>({
              domain: series.map((entry) => entry.label),
              range: series.map((entry) => entry.color),
            });

            return (
              <svg width={width} height={height}>
                <Group top={margin.top} left={margin.left}>
                  {/* Behind the series, so a boundary never obscures the data it is there to frame.
                    The icon that labels each one is drawn on the axis below, not here. */}
                  {visibleThresholds.map((marker) => (
                    <line
                      key={`band-threshold:${marker.tone}`}
                      x1={0}
                      x2={innerWidth}
                      y1={yScoreScale(marker.percent)}
                      y2={yScoreScale(marker.percent)}
                      stroke="#CBD5E1"
                      strokeWidth={1}
                      strokeDasharray="4 4"
                    />
                  ))}

                  {/* A bucket a line has no reading for shows as a gap in that line, and hovering
                    its column says so, in the words a measured point's tooltip uses with "not
                    measured" in place of the figure. Behind the lines, so a measured point in the
                    same column keeps its own tooltip. */}
                  {labels.map((label) => {
                    const unmeasured = series.filter((entry) => entry.points.find((point) => point.x === label)?.value === undefined);
                    if (!unmeasured.length) {
                      return null;
                    }

                    return (
                      <rect
                        key={`unmeasured:${label}`}
                        x={(xScale(label) || 0) + xScale.bandwidth() / 2 - xScale.step() / 2}
                        y={0}
                        width={xScale.step()}
                        height={innerHeight}
                        fill="transparent"
                      >
                        <title>{unmeasured.map((entry) => `${entry.label} (${label}): not measured`).join('\n')}</title>
                      </rect>
                    );
                  })}

                  {series.map((entry) => {
                    if (!entry.points.length) {
                      return null;
                    }

                    return (
                      <React.Fragment key={`line:${entry.label}`}>
                        {/* `defined` breaks the line across a bucket nobody measured, instead of
                          drawing through it — a segment spanning a gap would assert a trend
                          between two readings that has no data behind it. */}
                        <LinePath
                          data={entry.points}
                          defined={(point) => point.value !== undefined}
                          x={(point) => (xScale(point.x) || 0) + xScale.bandwidth() / 2}
                          y={(point) => yScoreScale(point.value ?? 0)}
                          stroke={colorScale(entry.label)}
                          strokeWidth={2.5}
                        />
                        {entry.points.map((point) =>
                          point.value === undefined ? null : (
                            <circle
                              key={`line-point:${entry.label}:${point.x}`}
                              cx={(xScale(point.x) || 0) + xScale.bandwidth() / 2}
                              cy={yScoreScale(point.value)}
                              r={5}
                              fill={colorScale(entry.label)}
                              stroke="#FFFFFF"
                              strokeWidth={3}
                            >
                              <title>{`${entry.label} (${point.x}): ${point.value}%`}</title>
                            </circle>
                          )
                        )}
                      </React.Fragment>
                    );
                  })}

                  <AxisLeft
                    scale={yScoreScale}
                    {...(bandsOnLeft ? { tickValues: axisTicks.values } : { numTicks: 5 })}
                    // At a boundary the icon IS the label, so the percentage is suppressed rather
                    // than printed beside it.
                    // A threshold tick renders its ICON only — the percentage is suppressed rather
                    // than printed beside it. Two labels on one tick is the crowding the icons were
                    // introduced to remove, and a reader cannot tell which of the two is the landmark.
                    tickFormat={(value) => bandedAxisTickLabel(Number(value), bandsOnLeft)}
                    stroke="#94A3B8"
                    tickStroke="#94A3B8"
                    tickLabelProps={() => ({ fill: '#64748B', fontSize: 11, textAnchor: 'end', dy: '0.32em' })}
                  />
                  {bandsOnRight ? (
                    <AxisRight
                      left={innerWidth}
                      scale={yScoreScale}
                      tickValues={visibleThresholds.map((marker) => marker.percent)}
                      // Icons only. This axis exists to say where the bands start, and a percentage
                      // here would read as a second scale for the unbanded series.
                      tickFormat={() => ''}
                      stroke="#94A3B8"
                      tickStroke="#94A3B8"
                    />
                  ) : null}
                  {/* The icons, on whichever axis carries the bands. Sized with room to spare so the
                    two-thumb Excellent glyph cannot clip. */}
                  {(bandsOnLeft || bandsOnRight) &&
                    visibleThresholds.map((marker) => (
                      <foreignObject
                        key={`band-icon:${marker.tone}`}
                        x={bandsOnRight ? innerWidth + 8 : -BAND_AXIS_GUTTER + 6}
                        y={yScoreScale(marker.percent) - BAND_ICON_BOX / 2}
                        width={BAND_ICON_BOX * 2}
                        height={BAND_ICON_BOX}
                        style={{ overflow: 'visible' }}
                      >
                        <span
                          className="inline-flex items-center"
                          style={{ height: BAND_ICON_BOX }}
                          title={`${marker.label} from ${marker.percent}% — ${marker.hint}`}
                          aria-label={`${marker.label} threshold at ${marker.percent}%`}
                        >
                          <BandThresholdAxisIcon glyph={marker.glyph} />
                        </span>
                      </foreignObject>
                    ))}
                  <AxisBottom
                    top={innerHeight}
                    scale={xScale}
                    stroke="#94A3B8"
                    tickStroke="#94A3B8"
                    tickLabelProps={() => ({ fill: '#64748B', fontSize: 11, textAnchor: 'middle', dy: '0.8em' })}
                  />
                </Group>
              </svg>
            );
          }}
        </ParentSize>
      </div>
    </div>
  );
}
